import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './ATMController.module.css';
import { 
    getAtmTerminals,
    updateAtmTerminal,
    UpdateAtmTerminalInput,
    getAtmProviders,
    AtmProvider,
    AtmCredentialInput,
    AtmTerminal,
    AtmCredential,
    AtmBalance
} from '../../services/atmTerminals.service';

interface AtmFormData {
    pinCode: number;
    phoneForOtp: string;
    port: string;
    recyclerLimitBalance: string;
    address: string;
    remoteId: string;
    userId: number;
}

interface BalanceInfo {
    recycler: AtmBalance | null;
    cashbox: AtmBalance | null;
}

const initialAtmFormState: AtmFormData = {
    pinCode: 0,
    phoneForOtp: '',
    port: '',
    recyclerLimitBalance: '0.00',
    address: '',
    remoteId: '',
    userId: 0,
};

const initialCredential: AtmCredentialInput = {
    atmProviderId: 0,
    agentName: '',
    agentPassword: '',
    agentKey: '',
};

const ATMEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [atmData, setAtmData] = useState<AtmFormData>(initialAtmFormState);
    const [credentials, setCredentials] = useState<AtmCredentialInput[]>([]);
    const [existingCredentials, setExistingCredentials] = useState<AtmCredential[]>([]);
    const [isEditingCredentials, setIsEditingCredentials] = useState(false);
    const [balanceInfo, setBalanceInfo] = useState<BalanceInfo>({ recycler: null, cashbox: null });
    const [providers, setProviders] = useState<AtmProvider[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [terminals, providersData] = await Promise.all([
                    getAtmTerminals(),
                    getAtmProviders()
                ]);
                
                setProviders(providersData);
                
                const terminal = terminals.find((t: AtmTerminal) => t.id === Number(id));
                if (terminal) {
                    setAtmData({
                        pinCode: terminal.pinCode,
                        phoneForOtp: terminal.phoneForOtp,
                        port: terminal.port,
                        recyclerLimitBalance: terminal.recyclerLimitBalance,
                        address: terminal.address,
                        remoteId: terminal.remoteId,
                        userId: terminal.userId,
                    });
                    
                    // Store balance info for read-only display
                    setBalanceInfo({
                        recycler: terminal.balanceRecycler,
                        cashbox: terminal.balanceCashbox,
                    });
                    
                    // Store existing credentials for display
                    if ((terminal as any).credentials) {
                        setExistingCredentials((terminal as any).credentials);
                    }
                } else {
                    setError('ATM Terminal not found');
                }
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load ATM terminal data');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchData();
        }
    }, [id]);

    const handleAtmInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = e.target;
        setAtmData((prev: AtmFormData) => ({
            ...prev,
            [name]: type === 'number' ? Number(value) : value,
        }));
    };

    const handleEditCredentials = () => {
        // Pre-populate with existing credentials when entering edit mode
        const editableCreds = existingCredentials.map((cred: AtmCredential) => ({
            atmProviderId: cred.atmProviderId,
            agentName: cred.agentName,
            agentPassword: '', // Password needs to be re-entered
            agentKey: cred.agentKey || '',
        }));
        setCredentials(editableCreds);
        setIsEditingCredentials(true);
    };

    const handleCancelEditCredentials = () => {
        setCredentials([]);
        setIsEditingCredentials(false);
    };

    const handleAddCredential = () => {
        setCredentials(prev => [...prev, { ...initialCredential }]);
    };

    const handleRemoveCredential = (index: number) => {
        setCredentials(prev => prev.filter((_, i) => i !== index));
    };

    const handleCredentialChange = (index: number, field: keyof AtmCredentialInput, value: string | number) => {
        setCredentials(prev => prev.map((cred, i) => 
            i === index ? { ...cred, [field]: field === 'atmProviderId' ? Number(value) : value } : cred
        ));
    };

    const isGameVaultProvider = (atmProviderId: number) => {
        const provider = providers.find(p => p.id === atmProviderId);
        return provider?.name?.toUpperCase() === 'GAMEVAULT';
    };

    const handleCancel = () => {
        navigate('/atm-controller');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setSubmitting(true);

        try {
            const updateInput: UpdateAtmTerminalInput = {
                id: Number(id),
                pinCode: atmData.pinCode,
                phoneForOtp: atmData.phoneForOtp,
                port: atmData.port,
                recyclerLimitBalance: atmData.recyclerLimitBalance,
                address: atmData.address,
                remoteId: atmData.remoteId,
                userId: atmData.userId,
                // Only include credentials if user explicitly edited them
                credentials: isEditingCredentials ? credentials : undefined,
            };

            await updateAtmTerminal(updateInput);
            setSuccess('ATM Terminal updated successfully!');
            setTimeout(() => {
                navigate('/atm-controller');
            }, 1500);
        } catch (err: any) {
            const errorMessage = err?.graphQLErrors?.[0]?.message || err?.message || 'Failed to update ATM terminal';
            setError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <main className={styles.main}>
                <div className={styles.headerRow}>
                    <h1 className={styles.title}>Edit ATM Terminal</h1>
                </div>
                <div className={styles.createFormContainer}>
                    <p>Loading...</p>
                </div>
            </main>
        );
    }

    return (
        <main className={styles.main}>
            <div className={styles.headerRow}>
                <h1 className={styles.title}>Edit ATM Terminal #{id}</h1>
            </div>

            <div className={styles.createFormContainer}>
                <form className={styles.form} onSubmit={handleSubmit}>
                    {error && <div className={styles.error}>{error}</div>}
                    {success && <div className={styles.success}>{success}</div>}

                    {/* ATM Terminal Section */}
                    <div className={styles.sectionTitle}>ATM Terminal Details</div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Remote ID *</label>
                        <input
                            type="text"
                            name="remoteId"
                            value={atmData.remoteId}
                            onChange={handleAtmInputChange}
                            className={styles.formInput}
                            placeholder="e.g., MY-ATM-01"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Address *</label>
                        <input
                            type="text"
                            name="address"
                            value={atmData.address}
                            onChange={handleAtmInputChange}
                            className={styles.formInput}
                            placeholder="ATM location address"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>PIN Code *</label>
                        <input
                            type="number"
                            name="pinCode"
                            value={atmData.pinCode || ''}
                            onChange={handleAtmInputChange}
                            className={styles.formInput}
                            placeholder="e.g., 1234"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Phone for OTP *</label>
                        <input
                            type="text"
                            name="phoneForOtp"
                            value={atmData.phoneForOtp}
                            onChange={handleAtmInputChange}
                            className={styles.formInput}
                            placeholder="e.g., +123456789"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Port *</label>
                        <input
                            type="text"
                            name="port"
                            value={atmData.port}
                            onChange={handleAtmInputChange}
                            className={styles.formInput}
                            placeholder="e.g., COM4"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Recycler Limit Balance *</label>
                        <input
                            type="text"
                            name="recyclerLimitBalance"
                            value={atmData.recyclerLimitBalance}
                            onChange={handleAtmInputChange}
                            className={styles.formInput}
                            placeholder="e.g., 200.00"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>User ID</label>
                        <input
                            type="number"
                            name="userId"
                            value={atmData.userId || ''}
                            onChange={handleAtmInputChange}
                            className={styles.formInput}
                            placeholder="User ID"
                            disabled
                        />
                    </div>

                    {/* Balance Section - Read Only */}
                    <div className={styles.sectionTitle}>Balances (Read Only)</div>

                    <div className={styles.balanceDisplayRow}>
                        <div className={styles.balanceDisplayCard}>
                            <div className={styles.balanceDisplayTitle}>Recycler</div>
                            <div className={styles.balanceDisplayValue}>
                                ${balanceInfo.recycler?.total || 0}
                            </div>
                            {balanceInfo.recycler?.bills && Object.keys(balanceInfo.recycler.bills).length > 0 && (
                                <div className={styles.balanceDisplayBills}>
                                    {Object.entries(balanceInfo.recycler.bills)
                                        .filter(([, count]) => count > 0)
                                        .map(([denom, count]) => (
                                            <span key={denom} className={styles.billChipSmall}>
                                                ${denom} × {count}
                                            </span>
                                        ))}
                                </div>
                            )}
                        </div>
                        <div className={styles.balanceDisplayCard}>
                            <div className={styles.balanceDisplayTitle}>Cashbox</div>
                            <div className={styles.balanceDisplayValue}>
                                ${balanceInfo.cashbox?.total || 0}
                            </div>
                            {balanceInfo.cashbox?.bills && Object.keys(balanceInfo.cashbox.bills).length > 0 && (
                                <div className={styles.balanceDisplayBills}>
                                    {Object.entries(balanceInfo.cashbox.bills)
                                        .filter(([, count]) => count > 0)
                                        .map(([denom, count]) => (
                                            <span key={denom} className={styles.billChipSmall}>
                                                ${denom} × {count}
                                            </span>
                                        ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Provider Credentials Section */}
                    <div className={styles.sectionTitleRow}>
                        <span className={styles.sectionTitle}>Provider Credentials</span>
                        {!isEditingCredentials && (
                            <button
                                type="button"
                                className={styles.editCredentialsBtn}
                                onClick={handleEditCredentials}
                            >
                                Edit Credentials
                            </button>
                        )}
                    </div>

                    {/* Read-only display when not editing */}
                    {!isEditingCredentials && (
                        <>
                            {existingCredentials.length > 0 ? (
                                existingCredentials.map((cred, index) => (
                                    <div key={index} className={styles.existingCredential}>
                                        <span>
                                            <strong>Provider:</strong> {providers.find(p => p.id === cred.atmProviderId)?.name || `ID: ${cred.atmProviderId}`}
                                        </span>
                                        <span><strong>Agent:</strong> {cred.agentName}</span>
                                        {cred.agentKey && <span><strong>Key:</strong> {cred.agentKey}</span>}
                                    </div>
                                ))
                            ) : (
                                <div className={styles.noCredentials}>No credentials configured</div>
                            )}
                        </>
                    )}

                    {/* Editable form when editing */}
                    {isEditingCredentials && (
                        <>
                            {credentials.map((cred, index) => (
                                <div key={index} className={styles.credentialRow}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>ATM Provider *</label>
                                        <select
                                            value={cred.atmProviderId || ''}
                                            onChange={(e) => handleCredentialChange(index, 'atmProviderId', e.target.value)}
                                            className={styles.formInput}
                                            required
                                        >
                                            <option value="">Select Provider</option>
                                            {providers.filter(p => p.isActive).map((provider) => (
                                                <option key={provider.id} value={provider.id}>
                                                    {provider.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Agent Name *</label>
                                        <input
                                            type="text"
                                            value={cred.agentName}
                                            onChange={(e) => handleCredentialChange(index, 'agentName', e.target.value)}
                                            className={styles.formInput}
                                            placeholder="Agent name"
                                            required
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Password *</label>
                                        <input
                                            type="password"
                                            value={cred.agentPassword}
                                            onChange={(e) => handleCredentialChange(index, 'agentPassword', e.target.value)}
                                            className={styles.formInput}
                                            placeholder="Password (required)"
                                            required
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>
                                            Agent Key {isGameVaultProvider(cred.atmProviderId) ? '*' : ''}
                                        </label>
                                        <input
                                            type="text"
                                            value={cred.agentKey || ''}
                                            onChange={(e) => handleCredentialChange(index, 'agentKey', e.target.value)}
                                            className={styles.formInput}
                                            placeholder={isGameVaultProvider(cred.atmProviderId) ? 'Agent key (required)' : 'Agent key (optional)'}
                                            required={isGameVaultProvider(cred.atmProviderId)}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.removeCredentialBtn}
                                        onClick={() => handleRemoveCredential(index)}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}

                            <div className={styles.credentialActions}>
                                <button
                                    type="button"
                                    className={styles.addCredentialBtn}
                                    onClick={handleAddCredential}
                                >
                                    + Add Credential
                                </button>
                                <button
                                    type="button"
                                    className={styles.cancelCredentialsBtn}
                                    onClick={handleCancelEditCredentials}
                                >
                                    Cancel Edit
                                </button>
                            </div>
                        </>
                    )}

                    <div className={styles.formActions}>
                        <button
                            type="button"
                            className={styles.cancelBtn}
                            onClick={handleCancel}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={styles.submitBtn}
                            disabled={submitting}
                        >
                            {submitting ? 'Updating...' : 'Update Terminal'}
                        </button>
                    </div>
                </form>
            </div>
        </main>
    );
};

export default ATMEdit;
